<template>
  <Dialog :open="true" @update:open="onOpenChange">
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Advertising Change Request</DialogTitle>
      </DialogHeader>

      <div class="bg-muted/50 p-4 rounded-lg text-sm">
        <header class="font-semibold">Reminder</header>
        <p class="mt-1 text-muted-foreground">
          Changes to Advertising and Billing are made at the 1st of each month. If you request a change mid-month, the
          changes will be reflected at the beginning of the next month.
        </p>
        <header class="mt-3 font-semibold">Current Total Spend</header>
        <p class="mt-1 text-muted-foreground">${{ currentTotalSpend }}</p>
      </div>

      <Field>
        <FieldLabel for="newly-requested-amount">Newly Requested Amount</FieldLabel>
        <CurrencyInput id="newly-requested-amount" v-model="totalSpend" />
      </Field>

      <Field>
        <FieldLabel for="change-reason">
          Change Request Reason
          <span class="text-muted-foreground font-normal">(optional)</span>
        </FieldLabel>
        <Textarea id="change-reason" v-model="changeReason" />
      </Field>

      <div class="flex items-start gap-2">
        <Checkbox id="management-agreement" v-model="managementAgreement" class="mt-0.5" />
        <Label for="management-agreement" class="text-sm font-normal leading-snug">
          By submitting this advertising change request, you are consenting to the advertising management fee set forth
          in your contract.
        </Label>
      </div>

      <p class="text-xs text-center leading-5 text-muted-foreground">
        This form submission is governed by Humaniz, Inc.'s
        <a href="https://www.humaniz.io/advertising-management-terms-of-service" target="_blank" class="underline">
          Terms and Conditions for Ad Management Services
        </a>
        .
      </p>

      <DialogFooter>
        <Button variant="outline" @click="emit('handleCancel')">Cancel</Button>
        <Button :disabled="isSubmitting" @click="submitChangeRequest">
          {{ isSubmitting ? 'Submitting...' : 'Submit' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { toast } from 'vue-sonner';
import { Api } from '@/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FieldLabel } from '@/components/ui/field';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

defineProps<{ currentTotalSpend: number }>();

const emit = defineEmits<{
  handleCancel: [];
  handleSave: [];
}>();

const totalSpend = ref<number | undefined>(undefined);
const changeReason = ref('');
const managementAgreement = ref(false);
const isSubmitting = ref(false);

const onOpenChange = (open: boolean) => {
  if (!open) emit('handleCancel');
};

const validateForm = () => {
  const errors: string[] = [];

  if (totalSpend.value === undefined || totalSpend.value <= 0) {
    errors.push('Amount must be greater than $0');
  }

  if (!managementAgreement.value) {
    errors.push('Consent required');
  }

  return errors;
};

const submitChangeRequest = async () => {
  const errors = validateForm();

  if (errors.length > 0) {
    toast.error('Please fix the following errors', { description: errors.join(', ') });
    return;
  }

  isSubmitting.value = true;
  const response = await Api.MonthlyMarketingBudgetUpdateRequests.createBudgetUpdateRequest({
    total_spend: totalSpend.value ?? 0,
    indeed_spend: 0,
    zip_spend: 0,
    monster_spend: 0,
    meta_spend: 0,
    reason: changeReason.value,
    management_fee_consent: managementAgreement.value,
  });
  isSubmitting.value = false;
  if (response.status === 'success') {
    toast.success('Advertising change request submitted', {
      action: { label: 'Dismiss' },
      description:
        'Your Advertising change request was successfully submitted.  Use the button below to dismiss this alert.',
    });
    emit('handleSave');
  } else {
    toast.error('Advertising change request error', {
      action: { label: 'Dismiss' },
      description: response.messages.join('\n'),
    });
  }
};
</script>
